import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    lineWidth: { type: Number, default: 2 },
    lineColor: { type: String, default: "#000000" },
    width: { type: Number, default: 400 },
    height: { type: Number, default: 150 }
  }

  connect() {
    this.canvas = this.element.querySelector("#signature-canvas")
    this.ctx = this.canvas.getContext("2d")
    
    // Set canvas dimensions
    this.canvas.width = this.widthValue
    this.canvas.height = this.heightValue
    
    // Configure context
    this.ctx.lineWidth = this.lineWidthValue
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
    this.ctx.strokeStyle = this.lineColorValue
    
    // State
    this.isDrawing = false
    this.lastX = 0
    this.lastY = 0
    this.history = []
    this.historyIndex = -1
    
    // Event listeners for mouse
    this.canvas.addEventListener("mousedown", this.handleStart.bind(this))
    this.canvas.addEventListener("mousemove", this.handleMove.bind(this))
    this.canvas.addEventListener("mouseup", this.handleEnd.bind(this))
    this.canvas.addEventListener("mouseout", this.handleEnd.bind(this))
    
    // Event listeners for touch
    this.canvas.addEventListener("touchstart", this.handleTouchStart.bind(this), { passive: false })
    this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this), { passive: false })
    this.canvas.addEventListener("touchend", this.handleEnd.bind(this))
    this.canvas.addEventListener("touchcancel", this.handleEnd.bind(this))
    
    // Clear button
    this.clearButton = this.element.querySelector("#clear-signature")
    if (this.clearButton) {
      this.clearButton.addEventListener("click", this.clear.bind(this))
    }
    
    // Undo button
    this.undoButton = this.element.querySelector("#undo-signature")
    if (this.undoButton) {
      this.undoButton.addEventListener("click", this.undo.bind(this))
    }
    
    // Color buttons
    this.element.querySelectorAll(".signature-color-btn").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.preventDefault()
        this.lineColorValue = btn.dataset.color
        this.ctx.strokeStyle = this.lineColorValue
        this.element.querySelectorAll(".signature-color-btn").forEach(b => b.classList.remove("ring-2"))
        btn.classList.add("ring-2")
      })
    })
    
    // Line width buttons
    this.element.querySelectorAll(".signature-width-btn").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.preventDefault()
        this.lineWidthValue = parseInt(btn.dataset.width)
        this.ctx.lineWidth = this.lineWidthValue
        this.element.querySelectorAll(".signature-width-btn").forEach(b => b.classList.remove("ring-2"))
        btn.classList.add("ring-2")
      })
    })
    
    // Initial state
    this.updateHiddenInput()
    this.setInitialStyles()
  }

  setInitialStyles() {
    // Highlight initial color button
    const initialColorBtn = this.element.querySelector(`[data-color="${this.lineColorValue}"]`)
    if (initialColorBtn) {
      initialColorBtn.classList.add("ring-2")
    }
  }

  getCanvasPoint(e) {
    const rect = this.canvas.getBoundingClientRect()
    const scaleX = this.canvas.width / rect.width
    const scaleY = this.canvas.height / rect.height
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    }
  }

  handleStart(e) {
    this.isDrawing = true
    const point = this.getCanvasPoint(e)
    this.lastX = point.x
    this.lastY = point.y
    
    // Save state for undo before starting new stroke
    this.saveState()
  }

  handleMove(e) {
    if (!this.isDrawing) return
    
    const point = this.getCanvasPoint(e)
    
    this.ctx.beginPath()
    this.ctx.moveTo(this.lastX, this.lastY)
    this.ctx.lineTo(point.x, point.y)
    this.ctx.stroke()
    
    this.lastX = point.x
    this.lastY = point.y
  }

  handleEnd(e) {
    if (!this.isDrawing) return
    this.isDrawing = false
    this.ctx.beginPath()
  }

  handleTouchStart(e) {
    e.preventDefault()
    if (e.touches.length === 1) {
      const touch = e.touches[0]
      this.isDrawing = true
      const point = this.getCanvasPoint(touch)
      this.lastX = point.x
      this.lastY = point.y
      this.saveState()
    }
  }

  handleTouchMove(e) {
    e.preventDefault()
    if (!this.isDrawing || e.touches.length !== 1) return
    
    const touch = e.touches[0]
    const point = this.getCanvasPoint(touch)
    
    this.ctx.beginPath()
    this.ctx.moveTo(this.lastX, this.lastY)
    this.ctx.lineTo(point.x, point.y)
    this.ctx.stroke()
    
    this.lastX = point.x
    this.lastY = point.y
  }

  saveState() {
    // Remove any future states (if we undid and then draw again)
    this.history = this.history.slice(0, this.historyIndex + 1)
    this.history.push(this.canvas.toDataURL())
    this.historyIndex++
    this.updateUndoButton()
  }

  undo() {
    if (this.historyIndex > 0) {
      this.historyIndex--
      const img = new Image()
      img.onload = () => {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.ctx.drawImage(img, 0, 0)
      }
      img.src = this.history[this.historyIndex]
      this.updateUndoButton()
      this.updateHiddenInput()
    }
  }

  updateUndoButton() {
    if (this.undoButton) {
      this.undoButton.disabled = this.historyIndex <= 0
      this.undoButton.classList.toggle("opacity-50", this.historyIndex <= 0)
    }
  }

  clear() {
    this.saveState()
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.updateHiddenInput()
  }

  updateHiddenInput() {
    let data = this.canvas.toDataURL("image/png")
    
    // Check if canvas is actually empty (all transparent)
    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height)
    const isEmpty = !imageData.data.some((channel, i) => i % 4 === 3 && imageData.data[i] !== 0)
    
    if (isEmpty) {
      data = ""
    }
    
    const input = this.element.querySelector("#signature-data")
    if (input) {
      input.value = data
    }
  }

  getData() {
    this.updateHiddenInput()
    const input = this.element.querySelector("#signature-data")
    return input ? input.value : ""
  }

  reset() {
    this.history = []
    this.historyIndex = -1
    this.clear()
    this.saveState() // Save initial blank state
  }

  disconnect() {
    this.canvas.removeEventListener("mousedown", this.handleStart)
    this.canvas.removeEventListener("mousemove", this.handleMove)
    this.canvas.removeEventListener("mouseup", this.handleEnd)
    this.canvas.removeEventListener("mouseout", this.handleEnd)
    this.canvas.removeEventListener("touchstart", this.handleTouchStart)
    this.canvas.removeEventListener("touchmove", this.handleTouchMove)
    this.canvas.removeEventListener("touchend", this.handleEnd)
    this.canvas.removeEventListener("touchcancel", this.handleEnd)
    
    if (this.clearButton) {
      this.clearButton.removeEventListener("click", this.clear)
    }
    if (this.undoButton) {
      this.undoButton.removeEventListener("click", this.undo)
    }
  }
}
